import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { daysOfWeek } from 'us-react-components/dist/constants/DaysOfWeek';
import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

interface WeekdayState {
  code: string;
  label: string;
  id: number;
  selected: boolean;
  startTime: string;
  endTime: string;
}

const INITIAL_WEEKDAYS: WeekdayState[] = daysOfWeek.map((day) => ({
  code: day.code ?? '',
  label: day.label ?? '',
  id: day.id ?? 0,
  selected: false,
  startTime: '',
  endTime: '',
}));

const LATEST_END_TIME = '23:45';

const hasTimeError = (day: WeekdayState): boolean =>
  !!day.startTime && !!day.endTime && (day.startTime >= day.endTime || day.endTime > LATEST_END_TIME);

const isDayComplete = (day: WeekdayState): boolean =>
  !!day.startTime && !!day.endTime && !hasTimeError(day);

const AVAILABILITY = [
  { code: 'Part Time', labelKey: 'pages.selectRecurringDates.availability.partTime', apiValue: 'part_time' },
  { code: 'Full Time', labelKey: 'pages.selectRecurringDates.availability.fullTime', apiValue: 'full_time' },
];

const TIMES_OF_DAY = [
  { code: '', id: -1, labelKey: 'pages.selectRecurringDates.timesOfDay.select' },
  { code: 'specific', id: 1, labelKey: 'pages.selectRecurringDates.timesOfDay.specific' },
  { code: 'day', id: 2, labelKey: 'pages.selectRecurringDates.timesOfDay.day' },
  { code: 'night', id: 3, labelKey: 'pages.selectRecurringDates.timesOfDay.night' },
];

const tomorrow = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(0, 0, 0, 0);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const SelectRecurringDates = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // parent signup-reason lookup deferred; start with Part-time as Angular does
  void useDependencies();

  const [weekdays, setWeekdays] = useState<WeekdayState[]>(INITIAL_WEEKDAYS);
  const [startDate, setStartDate] = useState<string>(tomorrow());
  const [endDate, setEndDate] = useState<string>('');
  const [timeOfDayId, setTimeOfDayId] = useState<number>(-1);
  const [flexible, setFlexible] = useState<boolean>(false);
  const [availabilityCode, setAvailabilityCode] = useState<string>('Part Time');

  const dateError = useMemo(() => {
    if (!endDate) return false;
    return new Date(endDate) <= new Date(startDate);
  }, [startDate, endDate]);

  const hasCorrectIntervals = ((): boolean => {
    const selected = weekdays.filter((day) => day.selected);
    if (selected.length === 0) return false;
    if (timeOfDayId !== 1) return true;
    return selected.every(isDayComplete);
  })();

  const isComplete =
    !dateError && timeOfDayId > 0 && (hasCorrectIntervals || flexible);

  useEffect(() => {
    document.title = 'UrbanSitter - Schedule';
  }, []);

  const toggleDay = (idx: number): void => {
    setWeekdays((prev) =>
      prev.map((day, index) => (index === idx ? { ...day, selected: !day.selected } : day)),
    );
  };

  const setIntervalTime = (idx: number, field: 'startTime' | 'endTime', value: string): void => {
    setWeekdays((prev) => {
      const updated = prev.map((day, index) => (index === idx ? { ...day, [field]: value } : day));
      const editedDay = updated[idx];
      const completeSelectedDays = updated.filter((day) => day.selected && isDayComplete(day));
      if (isDayComplete(editedDay) && completeSelectedDays.length === 1) {
        return updated.map((day) =>
          day.selected ? { ...day, startTime: editedDay.startTime, endTime: editedDay.endTime } : day,
        );
      }
      return updated;
    });
  };

  const continueToPost = (): void => {
    if (!isComplete) return;
    const params = new URLSearchParams(searchParams);
    const availability = AVAILABILITY.find((option) => option.code === availabilityCode)?.apiValue ?? 'part_time';
    params.set('availability', availability);
    params.set('fromNux', 'true');
    params.set('type', 'recurring');
    params.set('typeId', '1');
    navigate(`/job-post?${params.toString()}`);
  };

  const skipToSearch = (): void => {
    navigate('/sitters');
  };

  // id=1 is the 'specific' code in timesOfDay; only that option shows interval picker.
  const showIntervals = timeOfDayId === 1;

  return (
    <div className='bkgd-img-sitter-upside-down-child'>
      <div className='container py-8'>
        {/* mobile top CTA */}
        <div className='block sm:hidden mb-5'>
          <div className='us-card border-0 bg-transparent mb-5'>
            <h1 className='text-xl font-semibold mb-0'>{t('pages.selectRecurringDates.headingMobile')}</h1>
          </div>
          <div className='text-center mb-4'>
            <Button
              id='btn-check-availability-xs'
              buttonType={ButtonType.primary}
              onClick={continueToPost}
              disabled={!isComplete}
            >
              {t('pages.selectRecurringDates.next')}
            </Button>
            <div className='flex justify-center gap-2 mt-3'>
              {[0, 1, 2, 3].map((step) => (
                <span
                  key={step}
                  className={`w-2 h-2 rounded-full inline-block ${
                    step === 0 ? 'bg-emerald-500' : 'border border-neutral-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-12 gap-6'>
          <div className='sm:col-span-4 bg-white rounded p-5'>
            <h1 className='text-xl font-semibold mb-5 hidden sm:block'>
              {t('pages.selectRecurringDates.heading')}
            </h1>
            <h2 className='text-base mb-4'>{t('pages.selectRecurringDates.subheading')}</h2>

            <select
              id='availability'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-4'
              value={availabilityCode}
              onChange={(e): void => setAvailabilityCode(e.target.value)}
            >
              {AVAILABILITY.map((option) => (
                <option key={option.code} value={option.code}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>

            <input
              id='start-date'
              type='date'
              aria-label={t('pages.selectRecurringDates.startPlaceholder')}
              placeholder={t('pages.selectRecurringDates.startPlaceholder')}
              value={startDate}
              min={startDate}
              onChange={(e): void => setStartDate(e.target.value)}
              className={`form-control w-full border rounded px-3 py-2 mb-3 ${
                dateError ? 'border-red-500' : 'border-neutral-300'
              }`}
            />
            <input
              id='end-date'
              type='date'
              aria-label={t('pages.selectRecurringDates.endPlaceholder')}
              placeholder={t('pages.selectRecurringDates.endPlaceholder')}
              value={endDate}
              min={startDate}
              onChange={(e): void => setEndDate(e.target.value)}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
            />

            {dateError && (
              <div className='text-red-600 text-sm mb-3'>
                {t('pages.selectRecurringDates.dateError')}
              </div>
            )}

            <div className='font-normal mt-10 mb-2 text-sm'>
              {t('pages.selectRecurringDates.selectDaysHeading')}
            </div>
            <ul role='menu' className='flex gap-2 mb-4 p-0 list-none'>
              {weekdays.map((day, index) => (
                <li
                  key={day.code}
                  role='menuitem'
                  tabIndex={0}
                  onClick={(): void => toggleDay(index)}
                  onKeyUp={(e): void => {
                    if (e.key === 'Enter') toggleDay(index);
                  }}
                  className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer ${
                    day.selected
                      ? 'bg-sapphire text-white'
                      : 'border border-sapphire text-sapphire'
                  }`}
                >
                  {day.code[0]}
                </li>
              ))}
            </ul>

            <label
              htmlFor='flexible-dates'
              className='flex items-center gap-2 mb-10 cursor-pointer'
            >
              <input
                id='flexible-dates'
                type='checkbox'
                checked={flexible}
                onChange={(e): void => setFlexible(e.target.checked)}
              />
              <span>{t('pages.selectRecurringDates.daysFlexible')}</span>
            </label>

            <select
              id='timeOfDay'
              aria-label={t('pages.selectRecurringDates.timesOfDay.select')}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-5'
              value={timeOfDayId}
              onChange={(e): void => setTimeOfDayId(Number(e.target.value))}
            >
              {TIMES_OF_DAY.map((t2) => (
                <option key={t2.id} value={t2.id} disabled={!t2.code}>
                  {t(t2.labelKey)}
                </option>
              ))}
            </select>

            {showIntervals && (
              <div className='space-y-2 mb-5'>
                {weekdays
                  .filter((day) => day.selected)
                  .map((day) => {
                    const originalIndex = weekdays.findIndex((weekday) => weekday.code === day.code);
                    const timeError = hasTimeError(day);
                    return (
                      <div key={day.code}>
                        <div className='grid grid-cols-3 gap-2 items-center'>
                          <div className='text-sm'>{day.label}</div>
                          <input
                            type='time'
                            aria-label={`${day.label} ${t('pages.selectRecurringDates.intervalStart')}`}
                            value={day.startTime}
                            onChange={(e): void =>
                              setIntervalTime(originalIndex, 'startTime', e.target.value)
                            }
                            className={`form-control border rounded px-2 py-1 ${
                              timeError ? 'border-red-500' : 'border-neutral-300'
                            }`}
                          />
                          <input
                            type='time'
                            aria-label={`${day.label} ${t('pages.selectRecurringDates.intervalEnd')}`}
                            value={day.endTime}
                            onChange={(e): void =>
                              setIntervalTime(originalIndex, 'endTime', e.target.value)
                            }
                            className={`form-control border rounded px-2 py-1 ${
                              timeError ? 'border-red-500' : 'border-neutral-300'
                            }`}
                          />
                        </div>
                        {timeError && (
                          <div className='text-red-600 text-sm mt-1'>
                            {t('pages.selectRecurringDates.timeError')}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            <div className='text-center my-5'>
              <Button
                id='btn-check-availability'
                buttonType={ButtonType.primary}
                onClick={continueToPost}
                disabled={!isComplete}
              >
                {t('pages.selectRecurringDates.continue')}
              </Button>
              <Button
                buttonType={ButtonType.tertiary}
                id='skip-to-search'
                onClick={skipToSearch}
                className='block cursor-pointer mt-3 text-sm underline mx-auto'
              >
                {t('pages.selectRecurringDates.skipToSearch')}
              </Button>

              <div className='hidden sm:flex justify-center gap-2 mt-4'>
                {[0, 1, 2, 3].map((step) => (
                  <span
                    key={step}
                    className={`w-2 h-2 rounded-full inline-block ${
                      step === 3 ? 'bg-emerald-500' : 'border border-neutral-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
